import type { ResourceId } from '@/config/access-control'
import type { ConsoleUser, PlatformUser, ShowTabEntry, ShowTabsData } from '@/stores/auth'
import { selectShowTabs } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { isBytebeamStaff } from '@/utils/permissions'

interface LabDefinition {
	id: string
	name: string
	resource: ResourceId
	consentVersion: number
	consent: readonly string[]
}

const LABS_BY_ID = {
	ota_campaigns: {
		id: 'ota_campaigns',
		name: 'OTA Campaigns',
		resource: 'ota_campaigns',
		consentVersion: 1,
		consent: [
			'Can change without notice.',
			'Campaigns send firmware to devices.',
			'Everyone in this project will see it.',
		],
	},
	processors: {
		id: 'processors',
		name: 'Processors',
		resource: 'processors',
		consentVersion: 1,
		consent: [
			'Can change without notice.',
			'Processors read from a stream and write to other streams.',
			'Everyone in this project will see it.',
		],
	},
} as const satisfies Record<string, LabDefinition>

export const LABS = Object.values(LABS_BY_ID)

export type Lab = (typeof LABS)[number]

export type LabId = Lab['id']

export type LabUser = ConsoleUser | PlatformUser | null | undefined

export const isLabId = (key: string | undefined): key is LabId => LABS.some(lab => lab.id === key)

export const getLab = (id: LabId): Lab => LABS_BY_ID[id]

/** A stored `false` hides the lab from staff too. */
export const isLabEnabled = (id: LabId, showTabs: ShowTabsData | null | undefined, user: LabUser): boolean =>
	showTabs?.[id]?.show ?? isBytebeamStaff(user)

// A show_tabs entry is written whole, so both builders carry the stored entry forward.
export const turnLabOn = (entry: ShowTabEntry | undefined, lab: Lab, email: string, now: Date): ShowTabEntry => ({
	...entry,
	show: true,
	consent: { version: lab.consentVersion, accepted_at: now.toISOString(), accepted_by: email },
})

export const turnLabOff = (entry: ShowTabEntry | undefined): ShowTabEntry => ({ ...entry, show: false })

export function useLab(id: LabId): boolean {
	return useAuthStore(({ user }) => isLabEnabled(id, selectShowTabs(user), user))
}
